/**
 * InviteUserModal Component
 *
 * Modal for inviting a new user to the tenant.
 * Includes email validation and optional message field.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { useInviteUser } from '../hooks/useInviteUser';
import { useAuth } from '../../../contexts/AuthContext';
import { getErrorMessage } from '../../../lib/utils/errors';
import { emailSchema } from '../../../lib/validators/common';

// Validation schema
const inviteUserSchema = z.object({
  email: emailSchema,
  message: z.string().optional(),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const inviteUserMutation = useInviteUser(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
  });

  const onSubmit = async (data: InviteUserFormData) => {
    try {
      await inviteUserMutation.mutateAsync(data);
      reset();
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error('Failed to invite user:', error);
    }
  };

  const handleClose = () => {
    reset();
    inviteUserMutation.reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title>Invite User</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {inviteUserMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(inviteUserMutation.error)}
              </Alert>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                error={errors.email?.message}
                placeholder="user@example.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Personal Message (Optional)
              </label>
              <Textarea
                id="message"
                {...register('message')}
                placeholder="Add a personal message to the invitation email..."
                rows={4}
                disabled={isSubmitting}
              />
              <p className="mt-1 text-sm text-gray-500">
                This message will be included in the invitation email.
              </p>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Send Invitation
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
